/**
 * Step 13 (folder 17) — Paywall trial — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 3 step 13
 *
 * Current shape (2026-05-16 rewrite — Mason locks):
 *
 *   ┌─────────────────────────────────────────────────────────┐
 *   │   Free for 7 days. No catch.                            │ ← headline (Option C)
 *   │   Cancel in 2 taps. $̶1̶5̶/mo $12.49/mo after.              │ ← sub (price disclosure)
 *   │                                                          │
 *   │   ┌─── What's in your trial ───┐                        │
 *   │   │  ✓ Unlimited conversations  │                       │
 *   │   │  ✓ AI Insights on every verse                       │
 *   │   │  ✓ Greek / Hebrew / history lenses                  │
 *   │   │  ✓ All Learn lessons + certificates                 │
 *   │   └────────────────────────────┘                        │
 *   │                                                          │
 *   │   [ Start 7-day free trial → ]                          │
 *   │                                                          │
 *   │   First charge: May 23, 2026 · $12.49/mo                │ ← trust signals
 *   │   🔒 Secured by Stripe · Email 1 day before · Cancel 2  │
 *   └─────────────────────────────────────────────────────────┘
 *
 *   ┌─── Annual ────────┐  ┌─── Founding · LIMITED ─────┐
 *   │ $̶1̶4̶9̶.̶8̶8̶/yr           │  │ $̶1̶4̶9̶.̶8̶8̶/yr                │  ← CommitRow (Mason 2026-05-16)
 *   │ $99.99/yr           │  │ $79.99/yr                  │     cross-out + % off
 *   │ Save $49.89 · 33%  │  │ Save $69.89 · 47% off       │
 *   └──────────────────┘  │ X spots left · forever      │
 *                          └────────────────────────────┘
 *
 *   Continue with Free →
 *
 * Retired 2026-05-16:
 *   - Tower chart (Logos $279 / YouVersion Premium $9.99 / BibleProject+ $40
 *     anchors were stale, fabricated, or both — credibility risk)
 *   - "Your plan" + "You've already named your goal..." recap (felt like the
 *     onboarding was re-litigating itself before the pricing moment)
 *   - "You started something. Premium gives you the room..." headline + the
 *     "No commitments / no hidden fees" said-twice scaffolding (replaced by
 *     Option C founder-voice headline)
 *   - `premiumPriceLine` italic-serif callout (price now lives in TrustSignals)
 *   - Inline founding ribbon (replaced by CommitRow founding card)
 *   - Goal-tied checklist content (kept STRUCTURE — all 3 goals point at the
 *     same 4-bullet core today, so Max-tier slots can be added per goal later)
 *
 * Surviving Mason locks:
 *   - Literal first-charge date, computed from today + 7 days
 *   - Trust signals AT the card form (Stripe + cancel-in-2-taps)
 *   - "Spots-left" only shown when foundingSpotsLeft is a real number > 0
 *   - hasUsedTrial fallback (3-card paywall) preserved unchanged
 */

import { useEffect, useMemo, useState } from 'react'

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'
const TRIAL_DAYS = 7

// Compute the first-charge date — exactly 7 days from today. Rendered as
// "May 20, 2026" using user's locale. We compute inside useMemo so the
// string is stable across renders within a session.
function useFirstChargeDate() {
  return useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + TRIAL_DAYS)
    return d.toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }, [])
}

// Trial checklist — Premium capabilities surface here.
//
// ⚠️ TWO BULLETS BELOW ARE NOT YET BUILT (Mason 2026-05-16 decision):
//   - "Streak protection — rest days don't break your streak"
//     → tracked as `w1_streak_freezes` in roadmapData.js (~1 day to build)
//   - "Personalized devotional + Verse of the Day tuned to your journey"
//     → tracked as PRODUCTS.md Home Dashboard planned capabilities
//       (Devotional personalization + VotD AI personalization)
//
// Both MUST ship before this onboarding flow flips public — otherwise the
// paywall promises features that don't exist. Mason explicitly approved
// listing them now as forward-commitments; build follows.
//
// The other 4 bullets are strict-Premium today, pulled from PRODUCTS.md
// `Premium` tags.
//
// Goal-tied STRUCTURE preserved so future Max-tier adds — Sermon Prep for
// `lead_others`, Apologetics Sparring for `know_what_believe` — can be
// slotted in without restructuring. All 3 goals currently point at the
// same 6-bullet kit.
const TRIAL_CHECKLIST_CORE = [
  'Unlimited conversations with all 4 voices',
  'AI Insights on every verse in the Bible Reader',
  'Study any passage through Greek, Hebrew, cross-references, or early church history',
  'All Learn lessons unlocked + path certificates',
  'Streak protection — rest days don’t break your streak', // ⏳ unbuilt — w1_streak_freezes
  'Personalized devotional + Verse of the Day tuned to your journey', // ⏳ unbuilt — planned
]

const TRIAL_CHECKLIST_BY_GOAL = {
  grow_closer: TRIAL_CHECKLIST_CORE,
  know_what_believe: TRIAL_CHECKLIST_CORE,
  lead_others: TRIAL_CHECKLIST_CORE,
}

const DEFAULT_CHECKLIST = TRIAL_CHECKLIST_CORE

// Anchor tower chart retired 2026-05-16. Previous chart compared Illuminate
// to Logos $279, BibleProject+ $40/mo, YouVersion Premium $9.99/mo — but
// YouVersion has no Premium tier (donor-funded), BibleProject doesn't sell a
// $40/mo subscription, and the $279 Logos number refers to legacy perpetual
// packages most users don't buy now. Three wrong anchors = credibility risk.
// Replacement: no chart. Trial card carries the offer on its own. The
// personalized checklist below does the persuasion. See chat 2026-05-16.

// Commit row — slim "Already convinced? Save more." section beneath the trial
// hero. Locks Mason's 2026-05-16 trial-scoping decision: trial is MONTHLY ONLY,
// annual + founding are commit-now paths for conviction buyers. The old inline
// founding ribbon was passive info; this row makes it actionable.
//
// Layout: side-by-side cards (Annual / Founding) when foundingSpotsLeft > 0,
// or just Annual centered when founding is sold out.
function CommitRow({ foundingSpotsLeft, onChooseAnnual, onChooseFounding, visible }) {
  const showFounding = typeof foundingSpotsLeft === 'number' && foundingSpotsLeft > 0
  // Discount math — Mason 2026-05-16 picked a round $149.99 list-price anchor
  // (matches the $15/mo monthly list-price anchor used in the headline sub).
  // Standard SaaS "MSRP vs sale" framing; the strike price is a marketing
  // anchor, not a Stripe-charged amount.
  const LIST_ANNUAL = '$149.99'
  const cards = [
    {
      key: 'annual',
      label: 'Annual',
      strikePrice: LIST_ANNUAL,
      price: '$99.99',
      cadence: '/yr',
      savings: 'Save $50.00 · 33% off',
      badge: null,
      onClick: onChooseAnnual,
    },
  ]
  if (showFounding) {
    cards.push({
      key: 'founding',
      label: 'Founding',
      strikePrice: LIST_ANNUAL,
      price: '$79.99',
      cadence: '/yr',
      savings: 'Save $70.00 · 47% off',
      subline: `${foundingSpotsLeft} spots left · locked forever`,
      badge: 'LIMITED',
      onClick: onChooseFounding,
    })
  }
  return (
    <div
      className="mt-5"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 600ms ease-out 1100ms, transform 600ms ease-out 1100ms',
      }}
    >
      <p className="text-center text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] font-semibold mb-3">
        Already convinced? Save more.
      </p>
      <div
        className={`grid gap-3 ${cards.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 max-w-xs mx-auto'}`}
      >
        {cards.map(card => (
          <button
            key={card.key}
            type="button"
            onClick={card.onClick}
            className="relative text-left rounded-lg p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)]"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--glass-border)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
              cursor: 'pointer',
              transition: 'border-color 200ms ease-out, box-shadow 200ms ease-out',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--gold-dim)'
              e.currentTarget.style.boxShadow = '0 4px 12px -4px rgba(233,195,73,0.25)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--glass-border)'
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.2)'
            }}
          >
            {card.badge && (
              <span
                className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded"
                style={{
                  background: 'var(--gold-select)',
                  color: 'var(--gold)',
                  letterSpacing: '0.14em',
                }}
              >
                {card.badge}
              </span>
            )}
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--text-muted)]">
              {card.label}
            </p>
            {card.strikePrice && (
              <p className="text-[12px] text-[var(--text-muted-soft)] line-through tabular-nums mt-1 leading-tight">
                {card.strikePrice}
                <span className="text-[10px] font-normal"> {card.cadence}</span>
              </p>
            )}
            <p className="font-heading text-[20px] font-bold mt-0.5 text-[var(--text-primary)] tabular-nums">
              {card.price}
              <span className="text-[13px] font-normal text-[var(--text-muted)]">
                {' '}
                {card.cadence}
              </span>
            </p>
            <p className="text-[11px] text-[var(--gold)] mt-1 leading-snug font-semibold">
              {card.savings}
            </p>
            {card.subline && (
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-snug">
                {card.subline}
              </p>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
// Trust signal row — Stripe + cancel-in-2-taps. Mason lock.
// 2026-05-16: chart retired, so the post-trial price ($12.49/mo) now folds
// into the first-charge line — that's the only place the price disclosure
// lives in the card body. Required for honest trial enrollment.
// 2026-05-18: "Email 1 day before charge" line dropped from the row when
// the email-reminder pipeline was killed (incl. trial_ending email). If we
// rebuild a one-off trial-ending email later, restore the line here.
function TrustSignals({ firstChargeDate }) {
  return (
    <div className="mt-4 flex flex-col items-center gap-2 text-center">
      <p className="text-[12px] text-[var(--text-primary)] tracking-[0.005em]">
        <span className="font-semibold">First charge:</span>{' '}
        <span className="text-[var(--gold)] tabular-nums">{firstChargeDate}</span>
        <span className="text-[var(--text-muted)]"> · </span>
        <span className="text-[var(--gold)] font-semibold tabular-nums">$12.49/mo</span>
      </p>
      <div className="flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
        {/* Stripe wordmark (sans-mono, restrained — visual mark only) */}
        <span className="inline-flex items-center gap-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M13.479 9.883c-1.626-.605-2.512-1.077-2.512-1.795 0-.605.498-.953 1.384-.953 1.621 0 3.286.622 4.434 1.182l.658-4.058C16.547 3.708 14.86 3.2 12.351 3.2c-1.747 0-3.207.461-4.251 1.302-1.094.879-1.654 2.137-1.654 3.654 0 2.749 1.682 3.927 4.41 4.917 1.752.624 2.341 1.067 2.341 1.751 0 .664-.566 1.045-1.591 1.045-1.297 0-3.435-.633-4.811-1.446l-.659 4.103c1.181.665 3.371 1.339 5.633 1.339 1.85 0 3.395-.438 4.439-1.262 1.165-.92 1.769-2.279 1.769-4.034 0-2.823-1.722-3.992-4.498-5.034z" />
          </svg>
          <span className="uppercase tracking-[0.18em] font-semibold">Secured by Stripe</span>
        </span>
        <span aria-hidden="true">·</span>
        <span>Cancel in 2 taps</span>
      </div>
    </div>
  )
}

/* ----------------------------- Main ----------------------------- */
export default function PaywallTrialSynthesis({
  onStartTrial,
  onContinueFree,
  hasUsedTrial = false,
  copy,
  profile = {},
  foundingSpotsLeft,
  // Annual + founding handlers — separate from trial because trial-scoping
  // locked to monthly-only 2026-05-16. Default to onStartTrial so preview
  // works without explicit wiring; production overrides with real Stripe
  // checkout handoffs.
  onChooseAnnual,
  onChooseFounding,
}) {
  const handleAnnual = onChooseAnnual || onStartTrial
  const handleFounding = onChooseFounding || onStartTrial
  const [visible, setVisible] = useState(false)
  const [ctaHover, setCtaHover] = useState(false)
  const firstChargeDate = useFirstChargeDate()

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80)
    return () => clearTimeout(t)
  }, [])

  // hasUsedTrial — render the 3-card fallback (lifted from basic).
  if (hasUsedTrial) {
    return <UsedTrialFallback onStartTrial={onStartTrial} onContinueFree={onContinueFree} />
  }

  // Mason lock (f): personalized "What's in your trial" checklist by goal.
  const trialChecklist = TRIAL_CHECKLIST_BY_GOAL[profile.primary_goal] || DEFAULT_CHECKLIST

  return (
    <div className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      {/* "Your plan" eyebrow + "You've already" recap stripped 2026-05-16
          (Mason lock) — the user just spent 12 steps doing those things,
          they don't need to be re-cataloged before the pricing moment. */}

      {/* Main trial card */}
      <div
        role="group"
        aria-label="Premium 7-day free trial"
        className="relative rounded-2xl"
        style={{
          background: 'var(--bg-surface)',
          border: '1.25px solid var(--gold-dim)',
          boxShadow:
            'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 1px 2px rgba(0, 0, 0, 0.25), 0 8px 24px -12px rgba(0, 0, 0, 0.35)',
          padding: '24px 22px 26px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 600ms ease-out 120ms, transform 600ms ease-out 120ms',
        }}
      >
        {/* Trial headline — Mason 2026-05-16 picked Option C (Confidence /
            founder voice). Strips marketing-ese: "Free for 7 days. No catch."
            paired with one sub line that discloses the post-trial price and
            cancel mechanic in one breath. The old "You started something /
            Premium gives you the room" + duplicate "No commitments" lines
            were doing emotional work the personalized checklist below does
            better — and the post-trial price now lives in trust signals. */}
        <div className="text-center">
          <h1
            className="font-heading text-[24px] sm:text-[28px] font-extrabold text-[var(--text-primary)]"
            style={{ lineHeight: 1.22, letterSpacing: '-0.012em' }}
          >
            Free for 7 days. No catch.
          </h1>
          <p className="font-heading text-[15px] sm:text-[16px] text-[var(--text-muted)] mt-2 leading-[1.55] max-w-[34ch] mx-auto">
            Cancel in 2 taps.{' '}
            <span className="line-through text-[var(--text-muted-soft)] tabular-nums">$15/mo</span>{' '}
            <span className="text-[var(--gold)] font-semibold tabular-nums">$12.49/mo</span> after.
          </p>
        </div>

        {/* Anchor-tower chart retired 2026-05-16 (Mason lock). Replaced by:
            personalized checklist below + price disclosure in TrustSignals. */}

        {/* (f) Personalized "What's in your trial" checklist */}
        <section
          aria-label="What's included in your trial"
          className="mt-6 rounded-lg"
          style={{
            background: 'var(--bg-surface-high)',
            border: '1px solid var(--glass-border)',
            padding: '14px 16px 16px',
          }}
        >
          <p
            className="text-[10px] font-semibold uppercase text-[var(--gold)] mb-3"
            style={{ letterSpacing: '0.18em' }}
          >
            What&rsquo;s in your trial
          </p>
          <ul className="space-y-2">
            {trialChecklist.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-[13.5px] text-[var(--text-primary)] leading-[1.5]"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                  className="flex-shrink-0 mt-0.5"
                >
                  <path
                    d="M3 7.5L5.5 10L11 4"
                    stroke="var(--gold)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Founding ribbon retired 2026-05-16 — replaced by actionable
            CommitRow that renders OUTSIDE the trial card, beneath it.
            Trial scoping locked to monthly-only; annual + founding are
            commit-now paths, not "by the way" footnotes. */}

        {/* Primary CTA — "Start 7-day free trial" per Mason's locked label */}
        <div className="mt-6">
          <button
            type="button"
            onClick={onStartTrial}
            onMouseEnter={() => setCtaHover(true)}
            onMouseLeave={() => setCtaHover(false)}
            className="w-full font-semibold rounded-xl focus:outline-none"
            style={{
              padding: '14px 24px',
              fontSize: '15px',
              letterSpacing: '0.005em',
              background: ctaHover ? 'var(--gold-dim)' : 'var(--gold)',
              color: 'var(--text-on-gold)',
              border: '1px solid var(--gold)',
              boxShadow:
                'inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 6px 20px -10px var(--gold-select), 0 1px 2px rgba(0, 0, 0, 0.25)',
              cursor: 'pointer',
              transition: `background 200ms ${EASE}`,
            }}
          >
            <span className="inline-flex items-center justify-center gap-2">
              Start 7-day free trial
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  transform: ctaHover ? 'translateX(3px)' : 'translateX(0)',
                  transition: `transform 200ms ${EASE}`,
                }}
              >
                →
              </span>
            </span>
          </button>
        </div>

        {/* (b) Trust signals AT card form — Stripe + cancel-in-2-taps.
            (a) Literal first-charge date + $12.49/mo render inside this block.
            "No commitments / no hidden fees" duplicate lines retired 2026-05-16
            — the headline ("Free for 7 days. No catch.") + sub ("Cancel in 2
            taps. $12.49/mo after.") already carry that energy without the
            said-twice scaffolding. */}
        <TrustSignals firstChargeDate={firstChargeDate} />
      </div>

      {/* Commit row — "Already convinced? Save more." Annual + founding cards
          for conviction buyers. Trial = monthly-only (Mason 2026-05-16). */}
      <CommitRow
        foundingSpotsLeft={foundingSpotsLeft}
        onChooseAnnual={handleAnnual}
        onChooseFounding={handleFounding}
        visible={visible}
      />

      {/* Continue with Free — never punished */}
      <div className="mt-5 text-center">
        <button
          type="button"
          onClick={onContinueFree}
          className="text-[14px] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:underline focus:outline-none focus-visible:underline transition-colors"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 8px',
            textUnderlineOffset: '3px',
          }}
        >
          {copy.freeLink}
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* hasUsedTrial fallback — 3-card paywall (lifted from basic).        */
/* ------------------------------------------------------------------ */

function UsedTrialFallback({ onStartTrial, onContinueFree }) {
  const plans = [
    {
      key: 'free',
      label: 'Free',
      price: '$0',
      cadence: 'forever',
      blurb: '20 messages/month. Bible reader + basics.',
      cta: 'Continue with Free',
      onClick: onContinueFree,
      featured: false,
    },
    {
      key: 'monthly',
      label: 'Premium',
      price: '$12.49',
      cadence: '/mo',
      blurb: 'Unlimited messages + all Premium tools.',
      cta: 'Choose monthly',
      onClick: onStartTrial,
      featured: true,
    },
    {
      key: 'annual',
      label: 'Founding annual',
      price: '$79.99',
      cadence: '/yr',
      blurb: 'Locked forever. ~47% off monthly.',
      cta: 'Choose annual',
      onClick: onStartTrial,
      featured: false,
    },
  ]

  return (
    <div className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="text-center mb-5 sm:mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--text-primary)]">
          Pick your plan
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-2">
          Trial already used on this account. Choose what fits.
        </p>
      </div>

      <div className="space-y-3">
        {plans.map(p => (
          <div
            key={p.key}
            className={`rounded-xl p-4 sm:p-5 transition-colors duration-150 ${
              p.featured
                ? 'border-2 border-[var(--gold)] bg-[var(--gold)]/10'
                : 'border border-[var(--glass-border)] bg-[var(--bg-surface-high)]'
            }`}
          >
            <div className="flex items-baseline justify-between mb-1">
              <p
                className={`font-heading text-lg font-semibold ${
                  p.featured ? 'text-[var(--gold)]' : 'text-[var(--text-primary)]'
                }`}
              >
                {p.label}
              </p>
              <p className="text-[var(--text-primary)] font-semibold tabular-nums">
                {p.price}
                <span className="text-[var(--text-muted)] text-sm font-normal"> {p.cadence}</span>
              </p>
            </div>
            <p className="text-sm text-[var(--text-muted)] mb-3">{p.blurb}</p>
            <button
              onClick={p.onClick}
              className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-base)] ${
                p.featured
                  ? 'bg-[var(--gold)] hover:bg-[var(--gold-dim)] text-[var(--text-on-gold)]'
                  : 'bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-high)] text-[var(--text-primary)] border border-[var(--glass-border)]'
              }`}
            >
              {p.cta}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
